import Reveal from "../../components/Reveal"
import iconMap from "../../components/iconMap"
// Four levels, not three: this file sits in pages/Funnel/, one deeper than the
// components/ files that carry the same import.
import BRAND from "../../../../config/brand.json"

// The step badges, BY POSITION — step one gets the first entry whatever its
// text says. Worth knowing before reading meaning into any of them.
//
// The third was `bg-warning-light text-warning` until 2026-08-26. Because the
// assignment is positional rather than semantic, caution-amber landed on
// whatever the third step happens to say — and on the live funnel that is
// "Contingency: we win together", the sentence explaining how the firm gets
// paid. A warning colour on the one line whose job is reassurance was nobody's
// decision; it fell out of the ordering. Operator replaced it with the warm
// raspberry (act:b42153f6 follow-on).
//
// WHY AN INLINE STYLE AND NOT A UTILITY: warm is deliberately absent from
// Tailwind's @theme (brand_palette_parity_spec excludes it, and a token there
// would generate text- and decoration- utilities for a colour that fails the
// text bar on every ground). So there is no bg-warm-* utility to reach for,
// and there must not be. The colour is read from the palette SSOT instead —
// the same idiom ScrollProgressBar.jsx uses — because that spec also bans hex
// literals in the JS consumers.
//
// The raspberry is the GROUND and the navy is the TEXT, which is the only
// arrangement the non-text lock permits. Measured before choosing: navy on a
// 20% raspberry ground is 8.03:1, against 7.06 / 8.05 / 7.01 for the three
// pills as they shipped — so this is at or above every one of them, and far
// past the 4.5 bar. Strength was picked from a mock at 9/15/20/30%
// (reviews/2026-08-26-raspberry-recurrence/pills.html); 20% is where it stops
// reading as a faded version of its neighbours and becomes its own colour.
//
// color-mix over a raw value on purpose: it keeps ONE raspberry in the system
// and derives the tint, the same way the testimonial quote-card ground does in
// investigation.css. Where color-mix is unsupported the declaration is simply
// dropped and the pill renders navy text on the card's white — legible, just
// unstyled, which is the same posture that ground already takes.
const BADGES = [
  { className: "bg-info-light text-info" },
  { className: "bg-success-light text-success-dark" },
  {
    className: "text-brand",
    style: { background: `color-mix(in srgb, ${BRAND.colors.warm} 20%, #fff)` },
  },
]

export default function HowItWorks({ content = {} }) {
  const steps = content.how_it_works_steps
  if (!steps || steps.length === 0) return null

  return (
    <Reveal as="section" className="bg-white py-16">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="font-serif text-3xl font-bold text-center text-ink mb-3">How It Works</h2>
        {content.how_it_works_subtitle && (
          <p className="text-center text-body mb-12">{content.how_it_works_subtitle}</p>
        )}
        <div className="flex flex-col md:flex-row gap-8">
          {steps.map((s, i) => {
            // icon is optional + additive: an allowlisted name resolves to a
            // glyph rendered as a small BADGE on the numbered circle. Because
            // the badge is absolutely positioned over the always-present
            // circle, it adds no height — steps with and without an icon stay
            // vertically aligned (a partial set no longer drops the icon'd
            // step's title below the others). An unknown/blank icon falls back
            // to the number only (the model validator already rejects unknown
            // names on save; this stays defensive for legacy/draft data).
            const StepIcon = s.icon ? iconMap[s.icon] : null
            return (
              <div key={s.title} className="group flex-1 text-center">
                <div className="relative w-14 h-14 mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-full bg-brand/10 border-[3px] border-brand flex items-center justify-center">
                    <span className="font-serif text-lg font-bold text-brand">{i + 1}</span>
                  </div>
                  {StepIcon && (
                    <div
                      data-testid="step-icon"
                      className="absolute -top-1.5 -right-2 w-8 h-8 rounded-full bg-white border border-brand/40 shadow-sm flex items-center justify-center text-brand"
                    >
                      <StepIcon size={18} stroke={1.75} aria-hidden="true" />
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-lg font-bold text-ink mb-2">{s.title}</h3>
                <p className="text-sm text-body leading-relaxed mb-4">{s.desc}</p>
                {s.badge && (
                  <span
                    className={`inline-block text-xs font-semibold px-3.5 py-1.5 rounded-full ${BADGES[i % BADGES.length].className}`}
                    style={BADGES[i % BADGES.length].style}
                  >
                    {s.badge}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Reveal>
  )
}
